/**
 * The Document Picture-in-Picture window and the iframe inside it. The loader owns this because
 * `documentPictureInPicture` requires a top-level document with transient activation, which a
 * sandboxed iframe has no way to supply.
 *
 * The PiP window is same-origin with the integrator's page, so nothing sensitive runs in its own
 * document — it is a shell hosting the cross-origin sandboxed iframe.
 */
import { configureFrame } from './frame-attributes';
import { LoadingPlaceholder, PANEL_BG } from './loading-placeholder';
import { logger } from './logger';
import { isFromFrameMessage, type ControlMessage, type ToFrameMessage } from './protocol';

/** Minimal typing for the Document Picture-in-Picture API (absent from the standard TS DOM lib). */
interface DocumentPictureInPicture {
	requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
}
declare global {
	interface Window {
		documentPictureInPicture?: DocumentPictureInPicture;
	}
}

const DEFAULT_PIP_WIDTH = 265;
const DEFAULT_PIP_HEIGHT = 241;

export class PipController {
	private readonly pipWindow: Window;
	private readonly iframe: HTMLIFrameElement;
	private readonly origin: string;

	/** Placeholder component shown until the iframe reports ready. */
	private placeholder: LoadingPlaceholder | null;

	private constructor(
		pipWindow: Window,
		iframe: HTMLIFrameElement,
		origin: string,
		placeholder: LoadingPlaceholder
	) {
		this.pipWindow = pipWindow;
		this.iframe = iframe;
		this.origin = origin;
		this.placeholder = placeholder;
	}

	/** True on browsers that expose Document PiP. */
	static isSupported(): boolean {
		return typeof window !== 'undefined' && 'documentPictureInPicture' in window;
	}

	/**
	 * Open the PiP window and inject the pip iframe. Returns null if PiP is unsupported or the
	 * request fails (e.g. the gesture was already consumed). MUST be called synchronously within a
	 * user gesture.
	 */
	static async open(pipUrl: string, origin: string): Promise<PipController | null> {
		if (!PipController.isSupported()) return null;
		let pipWindow: Window;
		try {
			pipWindow = await window.documentPictureInPicture!.requestWindow({
				width: DEFAULT_PIP_WIDTH,
				height: DEFAULT_PIP_HEIGHT
			});
		} catch (err) {
			logger.warn('Document PiP unavailable; using in-frame controls.', err);
			return null;
		}

		const style = pipWindow.document.createElement('style');
		style.textContent = `html,body{margin:0;height:100%;overflow:hidden;background:${PANEL_BG}}iframe{border:0}`;
		pipWindow.document.head.appendChild(style);

		const placeholder = new LoadingPlaceholder(pipWindow.document);
		pipWindow.document.body.appendChild(placeholder.element);

		const iframe = pipWindow.document.createElement('iframe');
		Object.assign(iframe.style, {
			position: 'relative',
			width: '100%',
			height: '100%',
			display: 'block'
		} satisfies Partial<CSSStyleDeclaration>);
		configureFrame(iframe, pipUrl, 'pip');
		pipWindow.document.body.appendChild(iframe);

		return new PipController(pipWindow, iframe, origin, placeholder);
	}

	/** Fade out the loading stand-in. Called once the pip frame says it is up. */
	dismissPlaceholder(): void {
		this.placeholder?.dismiss();
	}

	/** Whether a message event genuinely originates from this frame. */
	isFromFrame(event: MessageEvent): boolean {
		return event.origin === this.origin && event.source === this.iframe.contentWindow;
	}

	/** Post a control-plane message to the frame with an exact target origin (never '*'). */
	post(message: ToFrameMessage, transfer?: Transferable[]): void {
		this.iframe.contentWindow?.postMessage(message, this.origin, transfer ?? []);
	}

	/** Subscribe to control-plane messages from the pip's iframe. Returns an unsubscribe fn. */
	onControlMessage(handler: (message: ControlMessage) => void): () => void {
		const listener = (event: MessageEvent) => {
			if (!this.isFromFrame(event)) return;
			if (isFromFrameMessage(event.data)) handler(event.data);
		};
		this.pipWindow.addEventListener('message', listener);
		return () => this.pipWindow.removeEventListener('message', listener);
	}

	/** Fires when the user (or the OS) closes the PiP window. Returns an unsubscribe fn. */
	onClose(handler: () => void): () => void {
		const listener = () => handler();
		this.pipWindow.addEventListener('pagehide', listener);
		return () => this.pipWindow.removeEventListener('pagehide', listener);
	}

	destroy(): void {
		this.placeholder?.destroy();
		this.placeholder = null;
		try {
			this.pipWindow.close();
		} catch {
			// Window may already be gone; ignore.
		}
	}
}
